<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from '@/composable/useI18n'

interface EnergyFieldsData {
  nominal_voltage: number
  min_utilization_current: number
  min_non_utilization_current: number
  current_calculation_ratio_phase_a: number
  current_calculation_ratio_phase_b: number
  current_calculation_ratio_phase_c: number
  voltage_calculation_ratio: number
  inverted_signal: boolean
}

interface Props {
  modelValue: EnergyFieldsData
}

interface Emits {
  (e: 'update:modelValue', value: EnergyFieldsData): void
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()
const { t } = useI18n()

const formData = computed({
  get: () => props.modelValue,
  set: (value) => emit('update:modelValue', value),
})
</script>

<template>
  <div class="space-y-6">
    <!-- Grupo: Tensão -->
    <div class="rounded-lg border border-gray-200 bg-gray-50/50 p-4">
      <h3 class="mb-3 text-sm font-semibold text-gray-700">Tensão</h3>
      <div>
        <label for="nominal_voltage" class="block text-sm font-medium"> Tensão nominal (V) </label>
        <input
          v-model.number="formData.nominal_voltage"
          type="number"
          step="0.1"
          id="nominal_voltage"
          class="mt-1 block w-full border border-bodydark2 px-3 py-2 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm" />
      </div>
    </div>

    <!-- Grupo: Corrente -->
    <div class="rounded-lg border border-gray-200 bg-gray-50/50 p-4">
      <h3 class="mb-3 text-sm font-semibold text-gray-700">Corrente</h3>
      <div class="grid grid-cols-2 gap-4">
        <div>
          <label for="min_utilization_current" class="block text-sm font-medium"> Corrente de utilização (A) </label>
          <input
            v-model.number="formData.min_utilization_current"
            type="number"
            step="0.1"
            id="min_utilization_current"
            class="mt-1 block w-full border border-bodydark2 px-3 py-2 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm" />
        </div>
        <div>
          <label for="min_non_utilization_current" class="block text-sm font-medium"> Corrente mínima de utilização (A) </label>
          <input
            v-model.number="formData.min_non_utilization_current"
            type="number"
            step="0.1"
            id="min_non_utilization_current"
            class="mt-1 block w-full border border-bodydark2 px-3 py-2 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm" />
        </div>
      </div>
    </div>

    <!-- Grupo: Sinal Invertido -->
    <div class="rounded-lg border border-gray-200 bg-gray-50/50 p-4">
      <div class="flex items-center gap-2">
        <input
          v-model="formData.inverted_signal"
          type="checkbox"
          id="inverted_signal"
          class="h-4 w-4 border-stroke text-primary focus:ring-primary" />
        <label for="inverted_signal" class="text-sm font-medium text-slate-700">{{ t('sensor.detail.info.inverted_signal') }}</label>
      </div>
    </div>

    <!-- Grupo: Ajuste de Corrente -->
    <div class="rounded-lg border border-gray-200 bg-gray-50/50 p-4">
      <h3 class="mb-3 text-sm font-semibold text-gray-700">Ajuste de Corrente</h3>
      <p class="mb-3 text-xs text-gray-500">Usado para calibrar valores de corrente por fase</p>
      <div class="grid grid-cols-2 gap-4">
        <div>
          <label for="current_calculation_ratio_phase_a" class="block text-sm font-medium"> Fase A </label>
          <input
            v-model.number="formData.current_calculation_ratio_phase_a"
            type="number"
            step="0.01"
            min="0"
            id="current_calculation_ratio_phase_a"
            placeholder="Opcional"
            class="mt-1 block w-full border border-bodydark2 px-3 py-2 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm" />
        </div>
        <div>
          <label for="current_calculation_ratio_phase_b" class="block text-sm font-medium"> Fase B </label>
          <input
            v-model.number="formData.current_calculation_ratio_phase_b"
            type="number"
            step="0.01"
            min="0"
            id="current_calculation_ratio_phase_b"
            placeholder="Opcional"
            class="mt-1 block w-full border border-bodydark2 px-3 py-2 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm" />
        </div>
        <div>
          <label for="current_calculation_ratio_phase_c" class="block text-sm font-medium"> Fase C </label>
          <input
            v-model.number="formData.current_calculation_ratio_phase_c"
            type="number"
            step="0.01"
            min="0"
            id="current_calculation_ratio_phase_c"
            placeholder="Opcional"
            class="mt-1 block w-full border border-bodydark2 px-3 py-2 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm" />
        </div>
      </div>
    </div>

    <!-- Grupo: Ajuste de Tensão -->
    <div class="rounded-lg border border-gray-200 bg-gray-50/50 p-4">
      <h3 class="mb-3 text-sm font-semibold text-gray-700">Ajuste de Tensão</h3>
      <p class="mb-3 text-xs text-gray-500">Usado para calibrar o valor de tensão</p>
      <div class="grid grid-cols-2 gap-4">
        <div class="col-span-2">
          <label for="voltage_calculation_ratio" class="block text-sm font-medium"> {{ t('sensor.detail.info.voltage_calculation_ratio') }} </label>
          <input
            v-model.number="formData.voltage_calculation_ratio"
            type="number"
            step="0.01"
            min="0"
            id="voltage_calculation_ratio"
            placeholder="Opcional"
            class="mt-1 block w-full border border-bodydark2 px-3 py-2 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm" />
        </div>
      </div>
    </div>
  </div>
</template>
